import * as i0 from '@angular/core';
import { EventEmitter, Directive, Input, Output, HostBinding, HostListener, inject, PLATFORM_ID, Injectable, Inject, Pipe, InjectionToken, SecurityContext } from '@angular/core';
import * as i1$1 from '@angular/router';
import { ActivatedRoute } from '@angular/router';
import { isPlatformBrowser, DOCUMENT } from '@angular/common';
import { BehaviorSubject, EMPTY, fromEvent, Subject, NEVER, ReplaySubject, filter, map, tap, takeUntil, take } from 'rxjs';
import { isObject, uniqBy } from 'lodash';
import clean from 'obj-clean';
import * as i1 from '@angular/platform-browser';
class FocusClickDirective {
  constructor() {
    // Allow the button to ignore click events when set to true
    this.disabled = false;
    // Allow the function passed by the host to be executed
    // when the emit() method gets called
    /**
     * This directive replaces the default `click` directive and allows the user to execute
     * the `click` event by clicking the mouse **and**  by using the `enter` key on focus.
     *
     * A tabindex of `0` gets added to the host.
     *
     * @memberof FocusClickDirective
     */
    this.focusClick = new EventEmitter();
    // Make every tag that uses this directive by default tabbable
    this.tabIndex = 0;
  }
  // Add eventhandler to the click event
  isClicked(event) {
    if (!this.disabled) {
      this.focusClick.emit(event);
    }
  }
  // Add eventhandler to keydown event When enter is pressed and the event
  // isn't blocked, execute the click function of the host
  isEntered() {
    if (!this.disabled) {
      this.focusClick.emit();
    }
  }
  static {
    this.ɵfac = function FocusClickDirective_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || FocusClickDirective)();
    };
  }
  static {
    this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: FocusClickDirective,
      selectors: [["", "focusClick", ""]],
      hostVars: 1,
      hostBindings: function FocusClickDirective_HostBindings(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵlistener("click", function FocusClickDirective_click_HostBindingHandler($event) {
            return ctx.isClicked($event);
          })("keydown.enter", function FocusClickDirective_keydown_enter_HostBindingHandler() {
            return ctx.isEntered();
          });
        }
        if (rf & 2) {
          i0.ɵɵattribute("tabindex", ctx.tabIndex);
        }
      },
      inputs: {
        disabled: "disabled"
      },
      outputs: {
        focusClick: "focusClick"
      }
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(FocusClickDirective, [{
    type: Directive,
    args: [{
      selector: '[focusClick]',
      standalone: true
    }]
  }], null, {
    disabled: [{
      type: Input
    }],
    focusClick: [{
      type: Output
    }],
    tabIndex: [{
      type: HostBinding,
      args: ['attr.tabindex']
    }],
    isClicked: [{
      type: HostListener,
      args: ['click', ['$event']]
    }],
    isEntered: [{
      type: HostListener,
      args: ['keydown.enter']
    }]
  });
})();
const Directives = [FocusClickDirective];
const getQueryParams = () => {
  return inject(ActivatedRoute).queryParams;
};

// @dynamic
class WindowService {
  /* eslint-enable */
  constructor(document,
  // tslint:disable-next-line: ban-types
  platformId) {
    this.document = document;
    this.platformId = platformId;
    /* eslint-disable @typescript-eslint/member-ordering */
    this.widthSubject$ = new BehaviorSubject(1200);
    this.scrollingUpSubject$ = new BehaviorSubject(true);
    this.currentScrollPositionSubject$ = new BehaviorSubject(0);
    /**
     * Observable to get the window-width, defaults to 1200 when no window is defined
     */
    this.width$ = this.widthSubject$.asObservable();
    /**
     * Observable to track when the scroll has ended
     */
    this.scrollingUp$ = this.scrollingUpSubject$.asObservable();
    /**
     * Observable of the current scroll position after the scroll has ended
     */
    this.currentScrollPosition$ = this.currentScrollPositionSubject$.asObservable();
    /**
     * Current scroll position after the scroll has ended
     */
    this.currentScrollPosition = 0;
    if (this.isBrowser() && this.hasDocument()) {
      this.window = this.document.defaultView;
      this.document.addEventListener('scroll', this.handleContentScroll.bind(this));
      this.widthSubject$.next(this.window.innerWidth);
      this.window.addEventListener('resize', () => {
        if (this.window.innerWidth && this.widthSubject$.getValue()) {
          this.widthSubject$.next(this.window.innerWidth);
        }
      });
    }
  }
  /**
   * Scrolls to the provided position of the page
   *
   * @param offset - Offset to which we want to scroll, scrolls to top when no offset is provided
   */
  scrollTo(offset = 0) {
    if (!this.window) {
      return;
    }
    this.window.scrollTo(0, offset);
  }
  /**
   * Returns whether there is a document present
   */
  hasDocument() {
    return !!this.document;
  }
  /**
   * Returns whether the current platform is a browser
   */
  isBrowser() {
    return isPlatformBrowser(this.platformId);
  }
  /**
   * Run a provided function only when we're in the browser and not in a server side rendered application
   *
   * @param action - Function we want to run in the browser
   */
  runInBrowser(action) {
    if (this.isBrowser) {
      action({
        browserWindow: this.window,
        browserDocument: this.document
      });
    } else {
      console.warn('Browser depended function has not run.');
    }
  }
  handleContentScroll() {
    if (window.pageYOffset > this.currentScrollPosition) {
      this.scrollingUpSubject$.next(false);
    } else {
      this.scrollingUpSubject$.next(true);
    }
    this.currentScrollPosition = window.pageYOffset < 0 ? 0 : window.pageYOffset;
    this.currentScrollPositionSubject$.next(this.currentScrollPosition);
  }
  static {
    this.ɵfac = function WindowService_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || WindowService)(i0.ɵɵinject(DOCUMENT), i0.ɵɵinject(PLATFORM_ID));
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: WindowService,
      factory: WindowService.ɵfac,
      providedIn: 'root'
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(WindowService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: Document,
    decorators: [{
      type: Inject,
      args: [DOCUMENT]
    }]
  }, {
    type: undefined,
    decorators: [{
      type: Inject,
      args: [PLATFORM_ID]
    }]
  }], null);
})();

/*
 * This windowMock constant return an object with a selected spy.
 * The reason why the spy is not provided by default is for this mock
 * to work on both Jest and Jasmine based test-suites.
 *
 * example:
 * windowMock(jasmine.createSpy());
 * */
const windowMock = spy => ({
  addEventListener: spy,
  defaultView: {
    scrollTo: () => null,
    innerWidth: 720,
    addEventListener: spy
  }
});
/*
 * Provide a spy for your testing framework because the windowMock requires it.
 *
 * The reason why the spy is not provided by default is for this mock
 * to work on both Jest and Jasmine based test-suites.
 *
 * example:
 * windowServiceMock(jasmine.createSpy(), 1440);
 * */
const windowServiceMock = (spy, width = 1200) => ({
  width: new BehaviorSubject(width),
  window: windowMock(spy),
  scrollTo: () => null,
  hasDocument: () => true,
  isBrowser: () => true,
  runInBrowser: callback => callback()
});

/**
 * A service that wraps the BroadCastChannel API and provides an Observable based implementation to the channel messages.
 *
 * For more information:
 * https://developer.mozilla.org/en-US/docs/Web/API/BroadcastChannel
 */
class NgxBroadcastChannelService {
  constructor(windowService) {
    this.windowService = windowService;
    /**
     * A record holding all the broadcast channels
     */
    this.broadcastChannel = {};
  }
  /**
   * initChannel
   *
   * The initChannel method initializes a new BroadcastChannel instance.
   *
   * @param args{ConstructorParameters<typeof BroadcastChannel>} - The arguments to pass to the BroadcastChannel constructor.
   */
  initChannel(...args) {
    // Iben: Only run when in browser
    this.windowService.runInBrowser(() => {
      const [channelName] = args;
      if (!channelName) {
        console.error('NgxUtils: There was an attempt to initialize a BroadcastChannel without providing a name.');
        return;
      }
      if (!this.broadcastChannel[channelName]) {
        this.broadcastChannel[channelName] = new BroadcastChannel(...args);
      }
    });
  }
  /**
   * closeChannel
   *
   * The closeChannel method closes a selected BroadcastChannel instance.
   *
   * @param channelName{string} - The name of the Broadcast Channel.
   */
  closeChannel(channelName) {
    if (!channelName || !this.broadcastChannel[channelName]) {
      return;
    }
    this.broadcastChannel[channelName].close();
    delete this.broadcastChannel[channelName];
  }
  /**
   * postMessage
   *
   * The postMessage method sends a message to a selected BroadcastChannel instance.
   *
   * @param channelName{string} - The name of the Broadcast Channel.
   * @param message{any} - The payload to send through the channel.
   */
  postMessage(channelName, message) {
    if (!channelName || !this.broadcastChannel[channelName]) {
      console.error('NgxUtils: There was an attempt to post a message to a channel without providing a name or the selected channel does not exist. The included message was:', message);
      return;
    }
    this.broadcastChannel[channelName].postMessage(message);
  }
  /**
   * selectChannelMessages
   *
   * The selectChannelMessages method subscribes to the `message` (bc.onmessage) event of a selected BroadcastChannel instance.
   *
   * @param channelName{string} - The name of the Broadcast Channel.
   * @returns Observable<MessageEvent> - The message event of the channel wrapped in an observable.
   */
  selectChannelMessages(channelName) {
    if (!channelName || !this.broadcastChannel[channelName]) {
      console.error("NgxUtils: There was an attempt to select a BroadcastChannel's messages without providing a name or the selected channel does not exist.");
      return EMPTY;
    }
    return fromEvent(this.broadcastChannel[channelName], 'message');
  }
  /**
   * selectChannelMessageErrors
   *
   * The selectChannelMessageErrors method subscribes to the `messageerror` (bc.onmessageerror) event of a selected BroadcastChannel instance.
   *
   * @param channelName{string} - The name of the Broadcast Channel.
   * @returns Observable<MessageEvent> - The messageerror event of the channel wrapped in an observable.
   */
  selectChannelMessageErrors(channelName) {
    if (!channelName || !this.broadcastChannel[channelName]) {
      console.error("NgxUtils: There was an attempt to select a BroadcastChannel's message errors without providing a name or the selected channel does not exist.");
      return EMPTY;
    }
    return fromEvent(this.broadcastChannel[channelName], 'messageerror');
  }
  static {
    this.ɵfac = function NgxBroadcastChannelService_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NgxBroadcastChannelService)(i0.ɵɵinject(WindowService));
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: NgxBroadcastChannelService,
      factory: NgxBroadcastChannelService.ɵfac,
      providedIn: 'root'
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NgxBroadcastChannelService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: WindowService
  }], null);
})();
class SubscriptionService {
  constructor() {
    this.destroyed$ = new Subject();
  }
  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
  static {
    this.ɵfac = function SubscriptionService_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || SubscriptionService)();
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: SubscriptionService,
      factory: SubscriptionService.ɵfac
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SubscriptionService, [{
    type: Injectable
  }], null, null);
})();

/**
 * A service that provides a SSR-proof Observable based approach to the session- and localStorage.
 */
class NgxStorageService {
  constructor(windowService) {
    this.windowService = windowService;
    /**
     * A record to hold the properties in the sessionStorage
     */
    this.sessionStorageRecord = {};
    /**
     * A record to hold the properties in the localStorage
     */
    this.localStorageRecord = {};
    /**
     * A subject to hold the events of the storage
     */
    this.storageEventSubject = new Subject();
    /**
     * An observable that emits whenever the session- or the localStorage was updated
     */
    this.storageEvents$ = this.storageEventSubject.asObservable();
    // Iben: Get the initial values of the session and the local storage
    windowService.runInBrowser(() => {
      this.setupStorage(sessionStorage, this.sessionStorageRecord);
      this.setupStorage(localStorage, this.localStorageRecord);
    });
  }
  /**
   * A localStorage implementation using observables
   */
  get localStorage() {
    return {
      getItem: key => this.getItem(key, localStorage),
      getItemObservable: key => this.getItemObservable(key, this.localStorageRecord),
      removeItem: key => this.removeItem(key, localStorage, this.localStorageRecord, 'local'),
      setItem: (key, item) => this.setItem(key, item, localStorage, this.localStorageRecord, 'local'),
      clear: () => this.clearStorage(localStorage, this.localStorageRecord, 'local')
    };
  }
  /**
   * A sessionStorage implementation using observables
   */
  get sessionStorage() {
    return {
      getItem: key => this.getItem(key, sessionStorage),
      getItemObservable: key => this.getItemObservable(key, this.sessionStorageRecord),
      removeItem: key => this.removeItem(key, sessionStorage, this.sessionStorageRecord, 'session'),
      setItem: (key, item) => this.setItem(key, item, sessionStorage, this.sessionStorageRecord, 'session'),
      clear: () => this.clearStorage(sessionStorage, this.sessionStorageRecord, 'session')
    };
  }
  getItem(key, storage) {
    return this.parseValue(storage.getItem(key));
  }
  /**
   * Returns an observable version of the storage value
   *
   * @param key - The key of the storage value
   * @param record  - The storage record
   */
  getItemObservable(key, record) {
    // Iben: Return NEVER when not in browser
    if (!this.windowService.isBrowser()) {
      return NEVER;
    }
    // Iben: If the subject already exists, we return the observable
    if (record[key]) {
      return record[key].asObservable();
    }
    // Iben: If no subject exits, we create a new one
    record[key] = new BehaviorSubject(undefined);
    // Iben: Return the observable
    return this.getItemObservable(key, record);
  }
  /**
   * Sets an item in the storage
   *
   * @param key - The key of the item
   * @param item - The item in the storage
   * @param storage - The storage in which we want to save the item
   * @param record - The corresponding storage record
   */
  setItem(key, item, storage, record, type) {
    // Iben: Early exit when we're in the browser
    if (!this.windowService.isBrowser()) {
      return undefined;
    }
    // Iben: Check if there's already a subject for this item. If not, we create one
    let subject = record[key];
    if (!subject) {
      subject = new BehaviorSubject(undefined);
      storage[key] = subject;
    }
    // Iben: Store the current value of the subject
    const oldValue = subject.getValue();
    // Iben: Set the item in the storage
    storage.setItem(key, typeof item === 'string' ? item : JSON.stringify(item));
    // Iben: Update the subject in the record
    subject.next(item);
    // Iben: Create the storage event
    const event = {
      key,
      newValue: item,
      oldValue,
      storage: type,
      type: 'set'
    };
    // Iben: Emit the storage event
    this.storageEventSubject.next(event);
    // Iben: Return the storage event
    return event;
  }
  /**
   * Remove an item from the storage and emit a remove event
   *
   * @param  key - The key of the item
   * @param storage - The storage we wish to remove the item from
   * @param record - The record with the subject
   * @param type - The type of storage
   */
  removeItem(key, storage, record, type) {
    // Iben: Early exit when we're not in the browser
    if (!this.windowService.isBrowser()) {
      return undefined;
    }
    // Iben: Get the old item
    const oldValue = this.parseValue(storage.getItem(key));
    // Iben: Remove the item from the storage
    storage.removeItem(key);
    // Iben Update the subject if it exists
    record[key]?.next(undefined);
    // Iben: Create the event and return and emit it
    const event = {
      oldValue,
      storage: type,
      key,
      type: 'remove'
    };
    this.storageEventSubject.next(event);
    return event;
  }
  /**
   * Clears the storage, completes all subjects and emits a clear event
   *
   * @param storage - The storage we wish to clear
   * @param record - The record with the subjects
   * @param type - The type of storage
   */
  clearStorage(storage, record, type) {
    // Iben: Early exit when we're not in the browser
    if (!this.windowService.isBrowser()) {
      return undefined;
    }
    // Iben: Clear the storage
    storage.clear();
    // Iben: Clear the record and complete all subjects
    Object.entries(record).forEach(([key, subject]) => {
      subject.next(undefined);
      subject.complete();
      record[key] = undefined;
    });
    // Iben: Create and emit event
    const event = {
      type: 'clear',
      storage: type
    };
    this.storageEventSubject.next(event);
    return event;
  }
  /**
   * Grabs the existing storage and updates the record
   *
   * @private
   * @param {Storage} storage - The current state of the storage
   * @param {NgxStorageRecord} record
   * @memberof NgxStorageService
   */
  setupStorage(storage, record) {
    Object.entries(storage).forEach(([key, value]) => {
      record[key] = new BehaviorSubject(this.parseValue(value));
    });
  }
  /**
   * Parses a string value from the storage to an actual value
   *
   * @param value - The provided string value
   */
  parseValue(value) {
    // Iben: If the value does not exist, return the value
    if (!value) {
      return value;
    }
    // Iben: If the value is either true or false, return a boolean version of the value
    if (value === 'true' || value === 'false') {
      return value === 'true';
    }
    // Iben: If the value is a number, return the parsed number
    if (value.match(/^[0-9]*[,.]{0,1}[0-9]*$/)) {
      return Number(value);
    }
    // Iben: If the value is an object, return the parsed object
    if (value.match(/{(.*:.*[,]{0,1})*}/)) {
      return JSON.parse(value);
    }
    // Iben: Return the string value as is
    return value;
  }
  static {
    this.ɵfac = function NgxStorageService_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NgxStorageService)(i0.ɵɵinject(WindowService));
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: NgxStorageService,
      factory: NgxStorageService.ɵfac,
      providedIn: 'root'
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NgxStorageService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: WindowService
  }], null);
})();

/**
 * A service that can be used to track media queries and their changes. It exposes a method
 * to register media queries, which takes an array of tuples with the id of the media query
 * and the query itself. The service will then emit the id of the media query that has
 * changed when subscribed to the `getMatchingQuery$` method.
 */
class NgxMediaQueryService {
  constructor(windowService) {
    this.windowService = windowService;
    /**
     * A map of media queries that are registered with the service.
     */
    this.queryListMap = new Map();
    /**
     * A map of the registered media queries with their id.
     */
    this.queryIdMap = new Map();
    /*
     * A map of listeners that are registered with the service.
     * They are saved to be able to remove them when the service is destroyed.
     */
    this.mediaQueryListenerMap = new Map();
    /**
     * A subject that emits the id of the media query that has changed.
     */
    this.queryChangedSubject = new ReplaySubject();
  }
  /**
   * Register a list of media queries that need to be tracked by the service.
   *
   * @param queries - A list of media queries that should be registered with the service.
   */
  registerMediaQueries(...queries) {
    this.windowService.runInBrowser(({
      browserWindow
    }) => {
      for (let [id, query] of queries) {
        // Wouter: Warn if the id has already been registered.
        if (this.queryIdMap.get(id)) {
          return console.warn(`NgxMediaQueryService: Media query with id '${id}' already exists and is defined by '${this.queryIdMap.get(id)}'`);
        }
        // Wouter: If the query has already been registered, throw an error to prevent duplicate subscriptions
        if ([...this.queryIdMap].some(([_, value]) => value === query)) {
          const duplicateQuery = [...this.queryIdMap].find(([_, value]) => value === query);
          throw new Error(`NgxMediaQueryService: Query of ['${id}', ${query}] already exists and is defined by ['${duplicateQuery[0]}', ${duplicateQuery[1]}]`);
        }
        // Wouter: save the id and query
        this.queryIdMap.set(id, query);
        // Wouter: For each query, create a MediaQueryList object
        const matchedQuery = browserWindow.matchMedia(query);
        // Wouter: Save the query
        this.queryListMap.set(id, matchedQuery);
        // Wouter: Emit the id of the query that has changed
        this.queryChangedSubject.next(id);
        // Wouter: Create a listener for the query. This is done separately to be
        // able to remove the listener when the service is destroyed
        const listener = queryChangedEvent => {
          this.queryListMap.set(id, queryChangedEvent.currentTarget);
          // Wouter: Emit the id of the query that has changed
          this.queryChangedSubject.next(id);
        };
        // Wouter: Register the listener to the query
        matchedQuery.addEventListener('change', listener);
        // Wouter: Save the listener
        this.mediaQueryListenerMap.set(id, listener);
      }
    });
  }
  /**
   * Pass the id of the query whose changes need to be listened to.
   *
   * @param id - The id of the media query that should be checked.
   * @returns An observable that emits a boolean value whenever the requested media query changes.
   */
  getMatchingQuery$(id) {
    // Wouter: Throw an error if the query has not been registered
    if (!this.queryIdMap.has(id)) {
      throw new Error(`NgxMediaQueryService: No media query with id '${id}' has been registered. Please register the media query first using the 'registerMediaQueries' method.`);
    }
    return this.queryChangedSubject.asObservable().pipe(
    // Wouter: Filter the query that has changed.
    // This will make sure only the [id] streams are triggered.
    filter(queryId => queryId === id), map(() => this.queryListMap.get(id).matches));
  }
  /**
   * Unregister all media query subscriptions from the service.
   */
  ngOnDestroy() {
    this.windowService.runInBrowser(() => {
      // Wouter: Remove all eventListeners
      for (let [id, query] of this.queryListMap) {
        query.removeEventListener('change', this.mediaQueryListenerMap.get(id));
      }
      // Wouter: Complete subscriptions
      this.queryChangedSubject.next(null);
      this.queryChangedSubject.complete();
      // Wouter: Clear maps
      this.queryListMap.clear();
      this.mediaQueryListenerMap.clear();
    });
  }
  static {
    this.ɵfac = function NgxMediaQueryService_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NgxMediaQueryService)(i0.ɵɵinject(WindowService));
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: NgxMediaQueryService,
      factory: NgxMediaQueryService.ɵfac,
      providedIn: 'root'
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NgxMediaQueryService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: WindowService
  }], null);
})();

/**
 * A pipe that will search an array to see if on of its objects contains specific values on provided keys.
 *
 * Usage:
 * value | arrayContainsOne : ['prop1', 'prop2', ...]
 *
 * Examples:
 * 	{{
 * 		[
 * 			{ title: 'This is the title', description: 'This is the description' },
 * 			{ title: 'This is the title' }
 * 		] | arrayContainsOne: ['description']
 * 	}}
 * 	Output: true
 */
class ArrayContainsOnePipe {
  transform(values, checkProps = []) {
    if (!Array.isArray(values) || values.length === 0 || !Array.isArray(checkProps) || checkProps.length === 0) {
      return false;
    }
    return values.some(item => checkProps.some(key => item[key] !== null && item[key] !== undefined));
  }
  static {
    this.ɵfac = function ArrayContainsOnePipe_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || ArrayContainsOnePipe)();
    };
  }
  static {
    this.ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
      name: "arrayContainsOne",
      type: ArrayContainsOnePipe,
      pure: true
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ArrayContainsOnePipe, [{
    type: Pipe,
    args: [{
      name: 'arrayContainsOne',
      standalone: true
    }]
  }], null, null);
})();
class BtwPipe {
  /**
   * Converts a BTW number to the correct format
   *
   * @param value - The value we wish to convert
   */
  transform(value) {
    if (!value) {
      // Denis: if the value is falsy, return it without transform.
      return value;
    }
    const addCharAtIndex = (original, char, index) => {
      return original.slice(0, index) + char + original.slice(index);
    };
    // Iben: Convert to string if it's a number
    value = value.toString();
    if (value.replace(/\./g, '').length === 9) {
      value = '0' + value;
    }
    // Iben: Format: xxxx.xxx.xxx
    if (value.charAt(4) !== '.') {
      value = addCharAtIndex(value, '.', 4);
    }
    if (value.charAt(8) !== '.') {
      value = addCharAtIndex(value, '.', 8);
    }
    return value;
  }
  static {
    this.ɵfac = function BtwPipe_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || BtwPipe)();
    };
  }
  static {
    this.ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
      name: "btw",
      type: BtwPipe,
      pure: true
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(BtwPipe, [{
    type: Pipe,
    args: [{
      name: 'btw',
      standalone: true
    }]
  }], null, null);
})();
class CleanArrayPipe {
  /**
   * Removes all falsy values from the provided array.
   *
   * @param value The values that need to be stripped of falsy values.
   * @param exceptions The falsy values that may be included in the filtered array.
   * @returns The filtered array.
   */
  transform(value, exceptions = []) {
    if (!exceptions.length) {
      return value.filter(Boolean);
    }
    return value.filter(entry => Boolean(entry) || exceptions.includes(entry));
  }
  static {
    this.ɵfac = function CleanArrayPipe_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || CleanArrayPipe)();
    };
  }
  static {
    this.ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
      name: "cleanArray",
      type: CleanArrayPipe,
      pure: true
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(CleanArrayPipe, [{
    type: Pipe,
    args: [{
      name: 'cleanArray',
      standalone: true
    }]
  }], null, null);
})();
class EntriesPipe {
  /**
   * Transforms a record into a [key, value] array
   *
   * @param value - The provided record
   */
  transform(value) {
    // Iben: If there's no value or the value is not an object, we return an empty array to prevent frontend breaking
    if (!value || !isObject(value) || Array.isArray(value)) {
      return [];
    }
    // Iben: Transform the record to a [key,value] array
    return Array.from(Object.entries(value));
  }
  static {
    this.ɵfac = function EntriesPipe_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || EntriesPipe)();
    };
  }
  static {
    this.ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
      name: "entries",
      type: EntriesPipe,
      pure: true
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(EntriesPipe, [{
    type: Pipe,
    args: [{
      name: 'entries',
      standalone: true
    }]
  }], null, null);
})();
class HasObserversPipe {
  transform(output) {
    return output && output.observers.length > 0;
  }
  static {
    this.ɵfac = function HasObserversPipe_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || HasObserversPipe)();
    };
  }
  static {
    this.ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
      name: "hasObservers",
      type: HasObserversPipe,
      pure: true
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(HasObserversPipe, [{
    type: Pipe,
    args: [{
      name: 'hasObservers',
      standalone: true
    }]
  }], null, null);
})();
class HasOwnProperty {
  /**
   * Checks whether the specified property exists within the given object.
   *
   * @param {unknown} object - The object to check for the presence of the property.
   * @param {string} prop - The property name to check for within the object.
   * @return {boolean} - Returns `true` if the property exists in the object, `false` otherwise.
   */
  transform(object, prop) {
    // Use Object.prototype.hasOwnProperty to check if the property exists in the object
    const hasOwnProperty = Object.prototype.hasOwnProperty;
    // Check if the object is not null or undefined, and if it has the specified property
    return object != null && hasOwnProperty.call(object, prop);
  }
  static {
    this.ɵfac = function HasOwnProperty_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || HasOwnProperty)();
    };
  }
  static {
    this.ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
      name: "hasOwnProperty",
      type: HasOwnProperty,
      pure: true
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(HasOwnProperty, [{
    type: Pipe,
    args: [{
      name: 'hasOwnProperty',
      standalone: true
    }]
  }], null, null);
})();
class HasValuesPipe {
  /**
   * Checks whether an object has values
   *
   * @param value - The provided value
   */
  transform(value) {
    // Iben: If the value is not an object, return valse
    if (!value || !isObject(value) || Array.isArray(value)) {
      return false;
    }
    // Iben: Return when the object has values
    return Object.keys(clean(value)).length > 0;
  }
  static {
    this.ɵfac = function HasValuesPipe_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || HasValuesPipe)();
    };
  }
  static {
    this.ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
      name: "hasValues",
      type: HasValuesPipe,
      pure: true
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(HasValuesPipe, [{
    type: Pipe,
    args: [{
      name: 'hasValues',
      standalone: true
    }]
  }], null, null);
})();
class HighlightPipe {
  /**
   * Highlights the provided substring of a test with the chosen dom element
   *
   * @param value - The full text with the part we wish to highlight
   * @param highlight - The part of the text we wish to highlight
   * @param domElement - The dom-element we wish to use to highlight, by default this is a `mark` element
   */
  transform(value, highlight, domElement = 'mark') {
    // Iben: Early exit if there's no value or the value is not a string
    if (!value || typeof value !== 'string') {
      return value;
    }
    // Iben: Match the highlight and replace it with the wrapped element
    const regexp = new RegExp(`(${highlight})`, 'i');
    return value.replace(regexp, `<${domElement}>$1</${domElement}>`);
  }
  static {
    this.ɵfac = function HighlightPipe_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || HighlightPipe)();
    };
  }
  static {
    this.ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
      name: "highlight",
      type: HighlightPipe,
      pure: true
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(HighlightPipe, [{
    type: Pipe,
    args: [{
      name: 'highlight',
      standalone: true
    }]
  }], null, null);
})();
class IbanPipe {
  transform(value = '') {
    value = value.replace(/\s/g, ''); // replace all spaces
    let reformat = value.replace(/(.{4})/g, function (match) {
      return match + ' '; // reformat into groups of 4 succeeded with a space
    });
    reformat = reformat.trim(); // remove trailing space
    return reformat;
  }
  static {
    this.ɵfac = function IbanPipe_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || IbanPipe)();
    };
  }
  static {
    this.ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
      name: "IBAN",
      type: IbanPipe,
      pure: true
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(IbanPipe, [{
    type: Pipe,
    args: [{
      name: 'IBAN',
      standalone: true
    }]
  }], null, null);
})();
class IsNotEmptyPipe {
  /**
   * Checks if a given argument is an object or array and if it is empty.
   *
   * @param value - can be any value.
   * @param checkProps - which props to check.
   */
  transform(value, checkProps = []) {
    // Denis: check for array first, because "typeof Array" will return "object"
    if (Array.isArray(value) && value.length > 0) {
      return true;
    }
    // Denis: check for null values second, because "typeof null" will return "object"
    if (value === null) {
      return false;
    }
    // Denis: if the object is not empty, and checkProps is provided, check those props.
    if (typeof value === 'object' && checkProps.length > 0) {
      const propsWithValues = checkProps.filter(key => typeof value[key] !== 'undefined' && value[key] !== null);
      return propsWithValues.length === checkProps.length;
    }
    // Denis: check for empty objects.
    if (typeof value === 'object' && Object.keys(value).length > 0) {
      return true;
    }
    return false;
  }
  static {
    this.ɵfac = function IsNotEmptyPipe_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || IsNotEmptyPipe)();
    };
  }
  static {
    this.ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
      name: "isNotEmpty",
      type: IsNotEmptyPipe,
      pure: true
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(IsNotEmptyPipe, [{
    type: Pipe,
    args: [{
      name: 'isNotEmpty',
      standalone: true
    }]
  }], null, null);
})();

// Iben: This implementation is from https://github.com/nglrx/pipes/blob/master/packages/pipes/src/lib/array/join/join.pipe.ts
// The package is no longer supported and is not compatible with more recent versions of Angular
class JoinPipe {
  /**
   * Transforms an array to a joined string
   *
   * @param values - An array of values
   * @param separator - A separator we wish to use
   */
  transform(values, separator) {
    return values && values.join(separator);
  }
  static {
    this.ɵfac = function JoinPipe_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || JoinPipe)();
    };
  }
  static {
    this.ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
      name: "join",
      type: JoinPipe,
      pure: true
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(JoinPipe, [{
    type: Pipe,
    args: [{
      name: 'join',
      standalone: true
    }]
  }], null, null);
})();
class LimitToPipe {
  /**
   * Limits an array to a specific value
   *
   * @param value - The provided array
   * @param limitedTo - The number to which we want to limit the array
   */
  transform(value, limitedTo) {
    // Iben: If no value is provided or the value is not an array, we early exit
    if (!value || !Array.isArray(value)) {
      return [];
    }
    // Iben: Slice the array based on the provided limit
    return [...value].slice(0, limitedTo);
  }
  static {
    this.ɵfac = function LimitToPipe_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || LimitToPipe)();
    };
  }
  static {
    this.ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
      name: "limitTo",
      type: LimitToPipe,
      pure: true
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(LimitToPipe, [{
    type: Pipe,
    args: [{
      name: 'limitTo',
      standalone: true
    }]
  }], null, null);
})();
class LogPipe {
  /**
   * Logs the provided value to the console.
   *
   * @param value The value to log to the console.
   * @param text An optional textual value to print before the piped value.
   */
  transform(value, text) {
    Boolean(text) ? console.log(text, value) : console.log(value);
  }
  static {
    this.ɵfac = function LogPipe_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || LogPipe)();
    };
  }
  static {
    this.ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
      name: "log",
      type: LogPipe,
      pure: true
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(LogPipe, [{
    type: Pipe,
    args: [{
      name: 'log',
      standalone: true
    }]
  }], null, null);
})();
class MergeArraysPipe {
  /**
   * The mergeArrays pipe will take a source array and concat it with all provided additional arrays.
   * Undefined or null values are filtered from the flattened result.
   *
   * @param source{any[]}
   * @param arrays{any[]}
   */
  transform(source = [], ...arrays) {
    // Denis: If the source is not a valid array, fallback to an empty array.
    if (!Array.isArray(source)) {
      return [];
    }
    return source
    // Denis: Concat the source with the provided arguments, filter out the non-array values from the arguments.
    .concat(...arrays.filter(array => Array.isArray(array)))
    // Denis: Filter undefined or null values from the flattened result.
    .filter(value => typeof value !== 'undefined' && value !== null);
  }
  static {
    this.ɵfac = function MergeArraysPipe_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || MergeArraysPipe)();
    };
  }
  static {
    this.ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
      name: "mergeArrays",
      type: MergeArraysPipe,
      pure: true
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(MergeArraysPipe, [{
    type: Pipe,
    args: [{
      name: 'mergeArrays',
      standalone: true
    }]
  }], null, null);
})();

/** The configuration token for the NgxReplaceElementsPipe */
const NgxReplaceElementsConfigurationToken = new InjectionToken('NgxReplaceElementsConfigurationToken');

/**
 * A pipe that allows to replace text elements with a WebComponent
 */
class NgxReplaceElementsPipe {
  constructor(configuration, sanitizer) {
    this.configuration = configuration;
    this.sanitizer = sanitizer;
  }
  /**
   * Replaces all matches of a specific selector with provided WebComponents
   *
   * @param value - The original string value
   * @param items - The items we wish to replace
   */
  transform(value, items) {
    // Iben: If the value isn't a string we early exit and warn the user
    if (typeof value !== 'string') {
      console.warn('NgxReplaceElements: A non string-value was provided to the NgxReplaceElementsPipe');
      return '';
    }
    // Iben: If no items were provided to replace, we just return the value
    if (!items || items.length === 0) {
      return value;
    }
    // Iben: set up a new instance of the DOMParser and parse the value as text/html.
    // This will return a Document which we can work with to find/replace elements.
    const parser = new DOMParser();
    const body = parser.parseFromString(value, 'text/html');
    // Iben: Loop over all items we wish to replace
    items.forEach(item => {
      // Iben: Get the selector and the element we want to replace the target with
      const {
        selector,
        element,
        includeInnerText
      } = this.configuration[item.elementId];
      // Iben: Select the target
      const target = body.querySelector(selector.replace('{{id}}', item.id));
      // Iben: If no target was found, early exit
      if (!target) {
        return;
      }
      // Iben: Create a new element within the Document based on the provided selector.
      // The selector can be any native or custom web component (not an Angular component).
      // Keep in mind that the element will need to have a lowercase input prop for the reference.
      const replacement = body.createElement(element);
      // Iben: If the item included data, we set these attributes
      if (item.data) {
        Object.entries(item.data).forEach(([key, value]) => {
          replacement.setAttribute(key, value);
        });
      }
      // Iben: Copy the innerText of the target element to the new element if needed.
      if (includeInnerText) {
        replacement.innerText = target.innerText;
      }
      // Iben: Replace the target with the new element within the Document.
      target.replaceWith(replacement);
    });
    // Iben: sanitize the document and mark it as trusted HTML before returning it to the template.
    return this.sanitizer.bypassSecurityTrustHtml(body.documentElement.innerHTML);
  }
  static {
    this.ɵfac = function NgxReplaceElementsPipe_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NgxReplaceElementsPipe)(i0.ɵɵdirectiveInject(NgxReplaceElementsConfigurationToken, 16), i0.ɵɵdirectiveInject(i1.DomSanitizer, 16));
    };
  }
  static {
    this.ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
      name: "ngxReplaceElements",
      type: NgxReplaceElementsPipe,
      pure: true
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NgxReplaceElementsPipe, [{
    type: Pipe,
    args: [{
      name: 'ngxReplaceElements',
      standalone: true
    }]
  }], () => [{
    type: undefined,
    decorators: [{
      type: Inject,
      args: [NgxReplaceElementsConfigurationToken]
    }]
  }, {
    type: i1.DomSanitizer
  }], null);
})();
class SafeHtmlPipe {
  constructor(sanitized) {
    this.sanitized = sanitized;
  }
  /**
   * Maps a provided string value to a sanitized string
   *
   * @param value - A provided string with HTML items
   */
  transform(value) {
    // Iben: Early exit in case the value is not a string
    if (!value || typeof value !== 'string') {
      return '';
    }
    // Iben: Sanitize the string
    return this.sanitized.sanitize(SecurityContext.HTML, value);
  }
  static {
    this.ɵfac = function SafeHtmlPipe_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || SafeHtmlPipe)(i0.ɵɵdirectiveInject(i1.DomSanitizer, 16));
    };
  }
  static {
    this.ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
      name: "safeHtml",
      type: SafeHtmlPipe,
      pure: true
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SafeHtmlPipe, [{
    type: Pipe,
    args: [{
      name: 'safeHtml',
      standalone: true
    }]
  }], () => [{
    type: i1.DomSanitizer
  }], null);
})();
class StripHtmlPipe {
  transform(value, replaceWith = '') {
    return value ? String(value).replace(/(<[^>]+>)+/gm, replaceWith).replace(/(&nbsp;)+/gm, replaceWith).replace(/(\r\n|\n|\r)+/gm, replaceWith) : '';
  }
  static {
    this.ɵfac = function StripHtmlPipe_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || StripHtmlPipe)();
    };
  }
  static {
    this.ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
      name: "stripHtml",
      type: StripHtmlPipe,
      pure: true
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(StripHtmlPipe, [{
    type: Pipe,
    args: [{
      name: 'stripHtml',
      standalone: true
    }]
  }], null, null);
})();
class ToArrayPipe {
  /**
   * Checks if a given argument is an array, if not, it will wrap the argument in a new array.
   */
  // Denis: Intentional any interface. The argument can be any kind of value.
  transform(value) {
    if (typeof value === 'undefined' || value === null) {
      return [];
    }
    if (Array.isArray(value)) {
      return value;
    }
    return [value];
  }
  static {
    this.ɵfac = function ToArrayPipe_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || ToArrayPipe)();
    };
  }
  static {
    this.ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
      name: "toArray",
      type: ToArrayPipe,
      pure: true
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ToArrayPipe, [{
    type: Pipe,
    args: [{
      name: 'toArray',
      standalone: true
    }]
  }], null, null);
})();

/**
 * A pipe to pass a transformer function to. By using this setup, we can use functions without causing rerender issues
 */
class TransformPipe {
  /**
   * Transforms a value based on a provided transform function
   *
   * @param value - The provided value we wish to transform
   * @param transformer - A provided transform function
   */
  transform(value, transformer) {
    // Iben: If no transformer is passed, we return the original value
    if (!transformer) {
      return value;
    }
    // Iben: Transform the value and return
    return transformer(value);
  }
  static {
    this.ɵfac = function TransformPipe_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || TransformPipe)();
    };
  }
  static {
    this.ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
      name: "transform",
      type: TransformPipe,
      pure: true
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(TransformPipe, [{
    type: Pipe,
    args: [{
      name: 'transform',
      standalone: true
    }]
  }], null, null);
})();
class TruncateTextPipe {
  transform(value, limit = 75) {
    return value.length > limit ? value.substring(0, limit) + '...' : value;
  }
  static {
    this.ɵfac = function TruncateTextPipe_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || TruncateTextPipe)();
    };
  }
  static {
    this.ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
      name: "truncateText",
      type: TruncateTextPipe,
      pure: true
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(TruncateTextPipe, [{
    type: Pipe,
    args: [{
      name: 'truncateText',
      standalone: true
    }]
  }], null, null);
})();
class UniqByPipe {
  transform(value, key) {
    if (!Array.isArray(value) || value.length === 0) {
      return [];
    }
    return uniqBy(value, key);
  }
  static {
    this.ɵfac = function UniqByPipe_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || UniqByPipe)();
    };
  }
  static {
    this.ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
      name: "uniqBy",
      type: UniqByPipe,
      pure: true
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(UniqByPipe, [{
    type: Pipe,
    args: [{
      name: 'uniqBy',
      standalone: true
    }]
  }], null, null);
})();
const WITH_ROUTER_LINKS_CONFIG = new InjectionToken('withRouterLinksConfig');

/**
 * An additional pipe to convert anchors to router-links
 */
class WithRouterLinkPipe {
  constructor(config, sanitizer) {
    this.config = config;
    this.sanitizer = sanitizer;
  }
  /**
   * transform
   *
   * The transform method loop through the linkReferences and find/replace matching
   * references within the given value (string) with the provided web component (global/local config).
   *
   * @param value
   * @param linkReferences
   */
  transform(value, linkReferences = []) {
    // Denis: Check if the provided value is a string,
    // if not, the DOMParser would fail, so return the value as given.
    if (typeof value !== 'string') {
      return value;
    }
    // Denis: set up a new instance of the DOMParser and parse the value as text/html.
    // This will return a Document which we can work with to find/replace elements.
    const parser = new DOMParser();
    const body = parser.parseFromString(value, 'text/html');
    // Denis: loop over each reference within the linkReferences.
    // The argument defaults to an empty array, if no linkReferences are provided,
    // it will loop over an empty array.
    linkReferences.forEach(({
      dataLinkId,
      link,
      replaceElementSelector,
      toAttribute,
      hostClass
    }) => {
      // Denis: construct the selector string
      const selector = `a[${this.config.dataLinkIdAttributeName}="${dataLinkId}"]`;
      // Denis: select the placeholder element within the parsed Document.
      const placeholderLink = body.querySelector(selector);
      // Denis: if no placeholder is found, early return.
      if (!placeholderLink) {
        return;
      }
      // Denis: create a new element within the Document based on the provided selector.
      // The selector can be any native or custom web component (not an Angular component).
      // Keep in mind that the element will need to have a lowercase input prop for the reference.
      // We will attempt to create the element with the replaceElementSelector (local)
      // and fall back to the config.replaceElementSelector (global).
      const parsedLink = body.createElement(replaceElementSelector || this.config.replaceElementSelector);
      // Denis: because setAttribute expects a string value,
      // check if the provided routerLinkValue is an Array. If so, join it.
      const routerLinkValue = Array.isArray(link) ? link.join('/') : link;
      // Denis: set the link attribute of the component.
      // We will attempt to set the attribute with the linkAttributeName (local)
      // and fall back to the config.linkAttributeName (global).
      parsedLink.setAttribute(toAttribute || this.config.linkAttributeName, routerLinkValue);
      // Wouter: If provided, set the hostClass provided by the pipe's transform. If no class is defined,
      // the global config will be used. If no global config is defined either, the class will not be set.
      if (hostClass || this.config.hostClass) {
        parsedLink.setAttribute('class', hostClass || this.config.hostClass);
      }
      // Denis: copy the innerText of the placeholder element to the new element.
      parsedLink.innerText = placeholderLink.innerText;
      // Denis: replace the placeholder with the new element within the Document.
      placeholderLink.replaceWith(parsedLink);
    });
    // Denis: sanitize the document and mark it as trusted HTML before returning it to the template.
    return this.sanitizer.bypassSecurityTrustHtml(body.documentElement.innerHTML);
  }
  static {
    this.ɵfac = function WithRouterLinkPipe_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || WithRouterLinkPipe)(i0.ɵɵdirectiveInject(WITH_ROUTER_LINKS_CONFIG, 16), i0.ɵɵdirectiveInject(i1.DomSanitizer, 16));
    };
  }
  static {
    this.ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
      name: "withRouterLinks",
      type: WithRouterLinkPipe,
      pure: true
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(WithRouterLinkPipe, [{
    type: Pipe,
    args: [{
      name: 'withRouterLinks',
      standalone: true
    }]
  }], () => [{
    type: undefined,
    decorators: [{
      type: Inject,
      args: [WITH_ROUTER_LINKS_CONFIG]
    }]
  }, {
    type: i1.DomSanitizer
  }], null);
})();
const Pipes = [ArrayContainsOnePipe, BtwPipe, CleanArrayPipe, CleanArrayPipe, EntriesPipe, HasObserversPipe, HasOwnProperty, HasValuesPipe, HighlightPipe, IbanPipe, IsNotEmptyPipe, JoinPipe, LimitToPipe, LogPipe, MergeArraysPipe, SafeHtmlPipe, StripHtmlPipe, ToArrayPipe, TransformPipe, TruncateTextPipe, UniqByPipe, WithRouterLinkPipe, NgxReplaceElementsPipe];
class NgxQueryParamFormSyncComponent {
  constructor(route, router) {
    this.route = route;
    this.router = router;
    this.destroyed$ = new Subject();
    /**
     * The query params we wish to form
     */
    this.queryParams$ = this.route.queryParams;
  }
  ngOnInit() {
    //Iben: Warn the user if one of the two methods isn't provided
    if (!this.scrambleParams && this.unscrambleParams || this.scrambleParams && !this.unscrambleParams) {
      console.error(`NgxUtils: NgxQueryParamFormSyncComponent detected the use of the parameter scrambling but is missing an implementation for the ${this.scrambleParams ? 'unscrambleParams' : 'scrambleParams'} method. Please provide this method in order for this flow to work correctly.`);
    }
    // Iben: Setup the form for the data
    this.form = this.initForm();
    // Iben: Listen to the form changes
    this.form.valueChanges.pipe(tap(data => {
      // Iben: Update the route params
      this.setDataInRoute(data);
      // Iben: Handle the route data changes
      if (this.handleDataChanges) {
        this.handleDataChanges(data);
      }
    }), takeUntil(this.destroyed$)).subscribe();
    // Iben: Listen to the initial query param update so we can set the data in the form if we navigate to a link with the params
    this.queryParams$.pipe(take(1), filter(Boolean), tap(data => {
      // Iben: Convert the route data properties to the actual data
      let value = Object.keys(data || {}).reduce((previous, current) => {
        return {
          ...previous,
          [current]: data[current] ? JSON.parse(data[current]) : undefined
        };
      }, {});
      // Iben: In case the unscrambleParams method is provided, we unscramble the data
      if (this.unscrambleParams) {
        value = this.unscrambleParams(value);
      }
      // Iben: Set the current form value
      this.form.setValue(value);
    }), takeUntil(this.destroyed$)).subscribe();
  }
  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
    this.clearData();
  }
  /**
   * Clears the data in the form
   */
  clearData() {
    this.form.reset();
  }
  /**
   * Sets the provided data in the route, so the filtered view can be shared by url
   *
   * @param  data - The provided data
   */
  setDataInRoute(data) {
    // Iben: If no data was provided, we simply unset the current params
    if (Object.keys(data || {}).length === 0) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {}
      });
      return;
    }
    // Iben: In case a scrambleParams function was provided, we scramble the params first
    const parsedData = this.scrambleParams ? this.scrambleParams(data) : data;
    // Iben: Stringify all properties of the data
    const queryParams = Object.keys(parsedData || {}).reduce((previous, current) => {
      return {
        ...previous,
        [current]: JSON.stringify(parsedData[current])
      };
    }, {});
    // Iben: Add the queryParams to the route
    this.router.navigate([], {
      relativeTo: this.route,
      queryParamsHandling: 'merge',
      queryParams
    });
  }
  static {
    this.ɵfac = function NgxQueryParamFormSyncComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NgxQueryParamFormSyncComponent)(i0.ɵɵdirectiveInject(i1$1.ActivatedRoute), i0.ɵɵdirectiveInject(i1$1.Router));
    };
  }
  static {
    this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: NgxQueryParamFormSyncComponent
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NgxQueryParamFormSyncComponent, [{
    type: Directive
  }], () => [{
    type: i1$1.ActivatedRoute
  }, {
    type: i1$1.Router
  }], null);
})();

/**
 * Provides the configuration for the NgxReplaceElements directive
 *
 * @param configuration - The required configuration
 */
const provideNgxReplaceElementsConfiguration = configuration => {
  return {
    provide: NgxReplaceElementsConfigurationToken,
    useValue: configuration
  };
};

/*
 * Public API Surface of utils
 */

/**
 * Generated bundle index. Do not edit.
 */

export { ArrayContainsOnePipe, BtwPipe, CleanArrayPipe, Directives, EntriesPipe, FocusClickDirective, HasObserversPipe, HasOwnProperty, HasValuesPipe, HighlightPipe, IbanPipe, IsNotEmptyPipe, JoinPipe, LimitToPipe, LogPipe, MergeArraysPipe, NgxBroadcastChannelService, NgxMediaQueryService, NgxQueryParamFormSyncComponent, NgxReplaceElementsConfigurationToken, NgxReplaceElementsPipe, NgxStorageService, Pipes, SafeHtmlPipe, StripHtmlPipe, SubscriptionService, ToArrayPipe, TransformPipe, TruncateTextPipe, UniqByPipe, WITH_ROUTER_LINKS_CONFIG, WindowService, WithRouterLinkPipe, getQueryParams, provideNgxReplaceElementsConfiguration, windowMock, windowServiceMock };
